import { ApiClient } from "api";
import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { defaultMetaObj, initialProjectDocumentState } from "store/constants";
import { NotificationManager } from "react-notifications";

const { projectDocumentEndPoint } = endPoints;

export const fetchAllProjectDocuments = createAsyncThunk(
  "projectDocument/fetchAllProjectDocuments",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: projectDocumentEndPoint,
      searchParams: { ...searchParamsObj },
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addProjectDocument = createAsyncThunk(
  "projectDocument/addProjectDocument",
  async ({ formData }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await ApiClient.post(
        `${projectDocumentEndPoint}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 204) {
        NotificationManager.success("Project Document Uploaded Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage, "", 10000);
      return rejectWithValue(error.message);
    }
  }
);

export const updateProjectDocument = createAsyncThunk(
  "projectDocument/updateProjectDocument",
  async ({ formData, id }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await ApiClient.put(
        `${projectDocumentEndPoint}/${id}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 200) {
        NotificationManager.success("Project Document Updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage, "", 10000);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProjectDocument = createAsyncThunk(
  "projectDocument/deleteProjectDocument",
  async ({ documentId }: { documentId: number }, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        `${projectDocumentEndPoint}`,
        documentId
      );

      if (response.status === 204) {
        NotificationManager.success("Document Deleted Successfully.");
        return response.data;
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage, "", 10000);
      return rejectWithValue(error.message);
    }
  }
);

const projectDocumentSlice = createSlice({
  name: "projectDocument",
  initialState: initialProjectDocumentState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all project documents
    builder
      .addCase(fetchAllProjectDocuments.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllProjectDocuments.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.projectDocuments = action.payload.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllProjectDocuments.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.meta = defaultMetaObj;
          state.projectDocuments = [];
        }
      );

    builder
      .addCase(addProjectDocument.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addProjectDocument.fulfilled, (state: any) => {
        state.loading = false;
      })
      .addCase(
        addProjectDocument.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // update project document

    builder
      .addCase(updateProjectDocument.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(updateProjectDocument.fulfilled, (state: any) => {
        state.loading = false;
      })
      .addCase(
        updateProjectDocument.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // Delete Project Document
    builder
      .addCase(deleteProjectDocument.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(deleteProjectDocument.fulfilled, (state: any) => {
        state.loading = false;
      })
      .addCase(
        deleteProjectDocument.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const projectDocumentRedcuer = projectDocumentSlice.reducer;
